import { lazy, Suspense, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { toast } from 'sonner'

import { ApiError, api } from '@/api/client'
import { CrmRealtimeListener } from '@/components/realtime-listener'
import { AppShell } from '@/components/shell'
import { LoadingBlock } from '@/components/ui'
import { RouteErrorPage } from '@/pages/route-error-page'

const DashboardPage = lazy(async () => {
  const module = await import('@/pages/dashboard-page')
  return { default: module.DashboardPage }
})

const SearchPage = lazy(async () => {
  const module = await import('@/pages/search-page')
  return { default: module.SearchPage }
})

const ProfilePage = lazy(async () => {
  const module = await import('@/pages/profile-page')
  return { default: module.ProfilePage }
})

const PartnerCabinetsPage = lazy(async () => {
  const module = await import('@/pages/partner-cabinets-page')
  return { default: module.PartnerCabinetsPage }
})

const PartnerCabinetWizardPage = lazy(async () => {
  const module = await import('@/pages/partner-cabinet-wizard-page')
  return { default: module.PartnerCabinetWizardPage }
})

const PartnerCabinetDetailPage = lazy(async () => {
  const module = await import('@/pages/partner-cabinet-detail-page')
  return { default: module.PartnerCabinetDetailPage }
})

const SquarePage = lazy(async () => {
  const module = await import('@/pages/square-page')
  return { default: module.SquarePage }
})

const CurrentSquarePage = lazy(async () => {
  const module = await import('@/pages/current-square-page')
  return { default: module.CurrentSquarePage }
})

const DirectoriesPage = lazy(async () => {
  const module = await import('@/pages/directories-page')
  return { default: module.DirectoriesPage }
})

const DirectoryDetailPage = lazy(async () => {
  const module = await import('@/pages/directory-detail-page')
  return { default: module.DirectoryDetailPage }
})

const DirectoryEntryEditPage = lazy(async () => {
  const module = await import('@/pages/directory-entry-edit-page')
  return { default: module.DirectoryEntryEditPage }
})

const ReportsPage = lazy(async () => {
  const module = await import('@/pages/reports-page')
  return { default: module.ReportsPage }
})

const ReportDetailPage = lazy(async () => {
  const module = await import('@/pages/report-detail-page')
  return { default: module.ReportDetailPage }
})

const ReportCreatePage = lazy(async () => {
  const module = await import('@/pages/report-create-page')
  return { default: module.ReportCreatePage }
})

const ReportBuilderPage = lazy(async () => {
  const module = await import('@/pages/report-builder-page')
  return { default: module.ReportBuilderPage }
})

const ApplicationDetailPage = lazy(async () => {
  const module = await import('@/pages/application-detail-page')
  return { default: module.ApplicationDetailPage }
})

const ApplicationFlowPage = lazy(async () => {
  const module = await import('@/pages/application-flow-page')
  return { default: module.ApplicationFlowPage }
})

const CatalogImportPage = lazy(async () => {
  const module = await import('@/pages/catalog-import-page')
  return { default: module.CatalogImportPage }
})

const LoginPage = lazy(async () => {
  const module = await import('@/pages/login-page')
  return { default: module.LoginPage }
})

const NotFoundPage = lazy(async () => {
  const module = await import('@/pages/not-found-page')
  return { default: module.NotFoundPage }
})

function LazyRoute({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LoadingBlock label="Открываем экран..." />}>{children}</Suspense>
}

function CreateFlowRoute() {
  return (
    <LazyRoute>
      <ApplicationFlowPage mode="create" />
    </LazyRoute>
  )
}

function EditFlowRoute() {
  return (
    <LazyRoute>
      <ApplicationFlowPage mode="edit" />
    </LazyRoute>
  )
}

function TransitionFlowRoute() {
  return (
    <LazyRoute>
      <ApplicationFlowPage mode="transition" />
    </LazyRoute>
  )
}

function ProtectedLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const sessionQuery = useQuery({
    queryKey: ['session'],
    queryFn: api.session,
  })
  const bootstrapQuery = useQuery({
    queryKey: ['bootstrap'],
    queryFn: api.bootstrap,
    enabled: !!sessionQuery.data?.authenticated,
  })

  useEffect(() => {
    if (!sessionQuery.isLoading && !sessionQuery.data?.authenticated) {
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, {
        replace: true,
      })
    }
  }, [location.pathname, location.search, navigate, sessionQuery.data, sessionQuery.isLoading])

  if (sessionQuery.isLoading || (sessionQuery.data?.authenticated && bootstrapQuery.isLoading)) {
    return <LoadingBlock label="Открываем рабочее пространство..." />
  }

  if (!sessionQuery.data?.authenticated || !bootstrapQuery.data) {
    return null
  }

  return (
    <>
      <CrmRealtimeListener />
      <AppShell
        bootstrap={bootstrapQuery.data}
        onLogout={async () => {
          try {
            await api.logout()
          } catch (error) {
            if (!(error instanceof ApiError) || ![401, 403].includes(error.status)) {
              toast.error(error instanceof Error ? error.message : 'Не удалось завершить сеанс.')
              return
            }
          }

          queryClient.clear()
          navigate('/login', { replace: true })
        }}
      />
    </>
  )
}

function LoginRoute() {
  const sessionQuery = useQuery({
    queryKey: ['session'],
    queryFn: api.session,
  })

  if (sessionQuery.isLoading) {
    return <LoadingBlock label="Проверяем сессию..." />
  }

  if (sessionQuery.data?.authenticated) {
    return <Navigate replace to="/" />
  }

  return (
    <LazyRoute>
      <LoginPage />
    </LazyRoute>
  )
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginRoute />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/',
    element: <ProtectedLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        index: true,
        element: (
          <LazyRoute>
            <DashboardPage />
          </LazyRoute>
        ),
      },
      {
        path: 'search',
        element: (
          <LazyRoute>
            <SearchPage />
          </LazyRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <LazyRoute>
            <ProfilePage />
          </LazyRoute>
        ),
      },
      {
        path: 'admin/partner-cabinets',
        element: (
          <LazyRoute>
            <PartnerCabinetsPage />
          </LazyRoute>
        ),
      },
      {
        path: 'admin/partner-cabinets/new',
        element: (
          <LazyRoute>
            <PartnerCabinetWizardPage />
          </LazyRoute>
        ),
      },
      {
        path: 'admin/partner-cabinets/:cityId',
        element: (
          <LazyRoute>
            <PartnerCabinetDetailPage />
          </LazyRoute>
        ),
      },
      {
        path: 'square/:squareId',
        element: (
          <LazyRoute>
            <SquarePage />
          </LazyRoute>
        ),
      },
      {
        path: 'current-square/:squareId',
        element: (
          <LazyRoute>
            <CurrentSquarePage />
          </LazyRoute>
        ),
      },
      {
        path: 'directories',
        element: (
          <LazyRoute>
            <DirectoriesPage />
          </LazyRoute>
        ),
      },
      {
        path: 'directories/:directoryId',
        element: (
          <LazyRoute>
            <DirectoryDetailPage />
          </LazyRoute>
        ),
      },
      {
        path: 'directory-entry/:entryId/edit',
        element: (
          <LazyRoute>
            <DirectoryEntryEditPage />
          </LazyRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <LazyRoute>
            <ReportsPage />
          </LazyRoute>
        ),
      },
      {
        path: 'reports/category/:categoryId',
        element: (
          <LazyRoute>
            <ReportsPage />
          </LazyRoute>
        ),
      },
      {
        path: 'reports/create',
        element: (
          <LazyRoute>
            <ReportCreatePage />
          </LazyRoute>
        ),
      },
      {
        path: 'reports/:reportId',
        element: (
          <LazyRoute>
            <ReportDetailPage />
          </LazyRoute>
        ),
      },
      {
        path: 'reports/:reportId/edit',
        element: (
          <LazyRoute>
            <ReportBuilderPage />
          </LazyRoute>
        ),
      },
      {
        path: 'application/:applicationId',
        element: (
          <LazyRoute>
            <ApplicationDetailPage />
          </LazyRoute>
        ),
      },
      {
        path: 'application/:applicationId/edit',
        element: <EditFlowRoute />,
      },
      {
        path: 'application/:applicationId/transition/:transitionId',
        element: <TransitionFlowRoute />,
      },
      {
        path: 'create-application/:schemaId/:stageId',
        element: <CreateFlowRoute />,
      },
      {
        path: 'catalog/import',
        element: (
          <LazyRoute>
            <CatalogImportPage />
          </LazyRoute>
        ),
      },
      {
        path: '*',
        element: (
          <LazyRoute>
            <NotFoundPage />
          </LazyRoute>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <LazyRoute>
        <NotFoundPage />
      </LazyRoute>
    ),
  },
])
